import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { Vocabulary } from "../Utils/Vocabulary";
import { getSupportTicketsPaginated } from "../Services/supportTicketsService";
import StatusChip from "./SupportTickets/StatusChip";
import PriorityChip from "./SupportTickets/PriorityChip";
import TicketFormModal from "./SupportTickets/TicketFormModal";
import TicketDetailsModal from "./SupportTickets/TicketDetailsModal";
import genericStyles from "../Styles/genericStyles.module.css";
import { statuses, priority, ticketType } from "../Utils/Utils";

const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    type: "",
    search: "",
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);


  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await getSupportTicketsPaginated({
        page,
        perPage,
        ...filters,
      });

      if (response?.data?.results) {
        setTickets(response.data.results);
        setTotalTickets(response?.data.numberOfResults || 0);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleOpenDetailsModal = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setDetailsModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedTicketId(null);
  };

  const handleSuccess = () => {
    fetchTickets();
  };

  const totalPages = Math.ceil(totalTickets / perPage);

  return (
    <div className={genericStyles.mainContainer}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">{Vocabulary.supportTickets}</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
          >
            {Vocabulary.createTicket}
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label={Vocabulary.search}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                size="small"
                label={Vocabulary.ticketStatus}
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                {statuses.map((status) => (
                  <MenuItem value={status.value}>{status.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                size="small"
                label={Vocabulary.ticketPriority}
                value={filters.priority}
                onChange={(e) => handleFilterChange("priority", e.target.value)}
              >
                {priority.map((priority) => (
                  <MenuItem value={priority.value}>{priority.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                size="small"
                label={Vocabulary.ticketType}
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                {ticketType.map((type) => (
                  <MenuItem value={type.value}>{type.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : tickets.length === 0 ? (
            <Typography align="center" color="textSecondary" sx={{ p: 4 }}>
              {Vocabulary.noTicketsFound}
            </Typography>
          ) : (
            <Box>
              {tickets.map((ticket: any) => (
                <Box
                  key={ticket._id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() => handleOpenDetailsModal(ticket._id)}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {ticket.ticketNumber}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {ticket.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <StatusChip status={ticket.status} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <PriorityChip priority={ticket.priority} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {Vocabulary.createdBy}:{" "}
                        {ticket.createdBy?.email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(ticket.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}
        </Paper>

        <TicketFormModal
          open={createModalOpen}
          onClose={handleCloseCreateModal}
          onSuccess={handleSuccess}
        />

        <TicketDetailsModal
          open={detailsModalOpen}
          onClose={handleCloseDetailsModal}
          ticketId={selectedTicketId}
          onSuccess={handleSuccess}
        />
      </Box>
    </div>
  );
};

export default SupportTickets;
